import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { MouseEvent, KeyboardEvent, FocusEvent } from 'react';
import type { Editor as TiptapEditor } from '@tiptap/core';
import styled, { css } from 'styled-components';
import { mix } from 'polished';

import {
  Popover,
  TouchDialog,
  Icon,
  registerIcon,
  useOuterEvent,
  Button,
  Menu,
  defaultThemeProp,
  useI18n,
  Tooltip,
  Flex,
  Text,
  isInstance,
  tryCatch,
  isTouchDevice
} from '@pega/cosmos-react-core';
import * as tableIcon from '@pega/cosmos-react-core/lib/components/Icon/icons/table.icon';

import useEscapeKey from '../hooks/useEscapeKey';
import useCloseOnEditorClick from '../hooks/useCloseOnEditorClick';

registerIcon(tableIcon);

const StyledTableButton = styled(Button)<{ $active?: boolean }>(({ theme, $active }) => {
  const activeColor = tryCatch(() =>
    mix(0.85, theme.base.palette['primary-background'], theme.base.palette.interactive)
  );
  return css`
    color: ${theme.base.palette['foreground-color']};
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: calc(3.5 * ${theme.base.spacing});
    min-width: calc(3.5 * ${theme.base.spacing});
    padding: 0;

    ${$active &&
    css`
      background-color: ${activeColor};
      color: ${theme.base.palette.interactive};
    `}
  `;
});

StyledTableButton.defaultProps = defaultThemeProp;

const StyledGridContainer = styled.div(({ theme }) => {
  return css`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    padding: ${theme.base.spacing};

    > div {
      flex: 1;
      min-height: 0;
    }
  `;
});

StyledGridContainer.defaultProps = defaultThemeProp;

const StyledGrid = styled.div(({ theme }) => {
  return css`
    display: grid;
    grid-template-columns: repeat(10, 1fr);
    gap: calc(0.25 * ${theme.base.spacing});
    margin-bottom: calc(0.5 * ${theme.base.spacing});
  `;
});

StyledGrid.defaultProps = defaultThemeProp;

const StyledCell = styled.button.attrs({ type: 'button' })<{ $isSelected?: boolean }>(
  ({ theme, $isSelected }) => {
    return css`
      width: calc(2 * ${theme.base.spacing});
      height: calc(2 * ${theme.base.spacing});
      border: calc(0.125 * ${theme.base.spacing}) solid
        ${theme.components['form-control']['border-color']};
      background-color: ${$isSelected
        ? theme.base.palette.interactive
        : theme.components['form-control']['background-color']};
      cursor: pointer;
      padding: 0;

      &:hover {
        background-color: ${mix(
          0.7,
          theme.base.palette.interactive,
          theme.base.palette['primary-background']
        )};
      }

      &:focus {
        outline: none;
        box-shadow: inset 0 0 0 calc(0.25 * ${theme.base.spacing}) ${theme.base.palette.interactive};
      }
    `;
  }
);

StyledCell.defaultProps = defaultThemeProp;

const StyledMenuContainer = styled.div(() => {
  return css`
    min-width: 12ch;

    > div:focus-visible {
      box-shadow: none;
    }
  `;
});

StyledMenuContainer.defaultProps = defaultThemeProp;

const StyledButtonGroup = styled.div(({ theme }) => {
  return css`
    display: flex;
    gap: calc(0.5 * ${theme.base.spacing});
    justify-content: space-between;
    margin: auto ${theme.base.spacing} 0;
    width: calc(100% - (2 * ${theme.base.spacing}));
    padding-block-end: ${theme.base.spacing};
  `;
});

StyledButtonGroup.defaultProps = defaultThemeProp;

interface TableButtonProps {
  editor: TiptapEditor;
  'data-testid'?: string;
}

const TableButton = ({ editor, ...restProps }: TableButtonProps) => {
  const t = useI18n();
  const [open, setOpen] = useState(false);
  const [hoveredCell, setHoveredCell] = useState<{ row: number; col: number } | null>(null);
  const [isInTable, setIsInTable] = useState(false);
  const [focusEditorAfterClose, setFocusEditorAfterClose] = useState(false);
  const [selectedSize, setSelectedSize] = useState<{ row: number; col: number }>({
    row: 1,
    col: 1
  });
  const isInTableRef = useRef(false);
  const justOpenedRef = useRef(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const touchDialogRef = useRef<HTMLDivElement>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const firstCellCallbackRef = useCallback(
    (node: HTMLButtonElement | null) => {
      if (node && open && !isInTable) {
        node.focus();
      }
    },
    [open, isInTable]
  );

  // Check if cursor is inside a table (check for tableCell since that's where cursor sits)
  const checkIsInTable = useCallback(() => {
    return (
      editor.isActive('table') || editor.isActive('tableCell') || editor.isActive('tableHeader')
    );
  }, [editor]);

  // Update state on editor changes - store in ref so it's never lost
  useEffect(() => {
    const updateState = () => {
      const inTable = checkIsInTable();
      isInTableRef.current = inTable;
      setIsInTable(inTable);
    };
    updateState();
    editor.on('selectionUpdate', updateState);
    editor.on('transaction', updateState);
    return () => {
      editor.off('selectionUpdate', updateState);
      editor.off('transaction', updateState);
    };
  }, [editor, checkIsInTable]);

  useOuterEvent('mousedown', [buttonRef, popoverRef, touchDialogRef], () => {
    if (open) {
      setOpen(false);
    }
  });

  const handleEscapeClose = useCallback(() => {
    setOpen(false);
    buttonRef.current?.focus();
  }, []);

  useEscapeKey(open, handleEscapeClose, editor);
  useCloseOnEditorClick(open, handleEscapeClose);

  const insertTable = useCallback(
    (rows: number, cols: number) => {
      if (isTouchDevice) {
        setFocusEditorAfterClose(true);
      }
      editor.chain().focus().insertTable({ rows, cols, withHeaderRow: false }).run();
      setOpen(false);
    },
    [editor]
  );

  const handleCellSelect = useCallback(
    (row: number, col: number) => {
      setSelectedSize({ row, col });
      if (!isTouchDevice) {
        insertTable(row + 1, col + 1);
      }
    },
    [insertTable]
  );

  const addRow = () => {
    editor.chain().focus().addRowAfter().run();
    setOpen(false);
  };

  const addColumn = () => {
    editor.chain().focus().addColumnAfter().run();
    setOpen(false);
  };

  const deleteRow = () => {
    editor.chain().focus().deleteRow().run();
    setOpen(false);
  };

  const deleteColumn = () => {
    editor.chain().focus().deleteColumn().run();
    setOpen(false);
  };

  const deleteTable = () => {
    editor.chain().focus().deleteTable().run();
    setOpen(false);
  };

  const getTableSizeLabel = (rows: number, cols: number) => `${rows} x ${cols}`;

  const handleCellKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          setSelectedSize(prev => ({ ...prev, col: Math.min(prev.col + 1, 9) }));
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setSelectedSize(prev => ({ ...prev, col: Math.max(prev.col - 1, 0) }));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedSize(prev => ({ ...prev, row: Math.min(prev.row + 1, 9) }));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedSize(prev => ({ ...prev, row: Math.max(prev.row - 1, 0) }));
          break;
        case 'Enter':
          e.preventDefault();
          setSelectedSize(prev => {
            insertTable(prev.row + 1, prev.col + 1);
            return prev;
          });
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          buttonRef.current?.focus();
          break;
        default:
          break;
      }
    },
    [insertTable]
  );

  // Memoize cells to avoid re-rendering all 100 cells on every state change
  const cells = useMemo(() => {
    const result: JSX.Element[] = [];
    for (let row = 0; row < 10; row += 1) {
      for (let col = 0; col < 10; col += 1) {
        const isHovered = hoveredCell && row <= hoveredCell.row && col <= hoveredCell.col;
        const isKeyboardSelected =
          !hoveredCell && row <= selectedSize.row && col <= selectedSize.col;
        const isFirstCell = row === 0 && col === 0;
        result.push(
          <StyledCell
            key={`${row}-${col}`}
            ref={isFirstCell ? firstCellCallbackRef : undefined}
            tabIndex={isFirstCell ? 0 : -1}
            $isSelected={isHovered || isKeyboardSelected}
            onMouseEnter={() => setHoveredCell({ row, col })}
            onClick={() => handleCellSelect(row, col)}
            onKeyDown={handleCellKeyDown}
            aria-label={t('rte_table_size', [row + 1, col + 1])}
          />
        );
      }
    }
    return result;
  }, [hoveredCell, selectedSize, firstCellCallbackRef, handleCellKeyDown, handleCellSelect, t]);

  // Reset selected size to 2x2 default when opening
  useEffect(() => {
    if (open && !isInTable) {
      setSelectedSize({ row: 1, col: 1 });
    }
  }, [open, isInTable]);

  const getGridLabel = () => {
    if (hoveredCell) {
      return getTableSizeLabel(hoveredCell.row + 1, hoveredCell.col + 1);
    }
    if (selectedSize) {
      return getTableSizeLabel(selectedSize.row + 1, selectedSize.col + 1);
    }
    return '1 x 1';
  };
  const gridLabel = getGridLabel();

  const handleCancel = () => {
    setFocusEditorAfterClose(false);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const tableContent = isInTable ? (
    <StyledMenuContainer>
      <Menu
        ref={menuRef}
        focusControlEl={isTouchDevice ? undefined : buttonRef.current ?? undefined}
        items={[
          { id: 'add-row', primary: t('rte_add_row') },
          { id: 'add-column', primary: t('rte_add_column') },
          { id: 'delete-row', primary: t('rte_delete_row') },
          { id: 'delete-column', primary: t('rte_delete_column') },
          { id: 'delete-table', primary: t('rte_delete_table') }
        ]}
        onItemClick={(id: string) => {
          switch (id) {
            case 'add-row':
              addRow();
              break;
            case 'add-column':
              addColumn();
              break;
            case 'delete-row':
              deleteRow();
              break;
            case 'delete-column':
              deleteColumn();
              break;
            case 'delete-table':
              deleteTable();
              break;
            default:
              break;
          }
        }}
      />
    </StyledMenuContainer>
  ) : (
    <StyledGridContainer>
      <Flex container={{ direction: 'column', gap: 1 }}>
        {!isTouchDevice && <Text variant='h2'>{t('rte_insert_table')}</Text>}
        <StyledGrid ref={gridContainerRef} onMouseLeave={() => setHoveredCell(null)}>
          {cells}
        </StyledGrid>
        <Text variant='secondary'>{gridLabel}</Text>
        <StyledButtonGroup>
          <Button variant='secondary' onClick={handleCancel}>
            {t('cancel')}
          </Button>
          <Button
            variant='primary'
            onClick={() => insertTable(selectedSize.row + 1, selectedSize.col + 1)}
          >
            {t('submit')}
          </Button>
        </StyledButtonGroup>
      </Flex>
    </StyledGridContainer>
  );

  return (
    <>
      <StyledTableButton
        {...restProps}
        variant='simple'
        type='button'
        icon={false}
        ref={buttonRef}
        data-toolbar-item='true'
        $active={isInTable}
        onMouseDown={(e: MouseEvent) => {
          e.preventDefault();
        }}
        onKeyDown={(e: KeyboardEvent) => {
          if (e.key === 'Escape' && open) {
            e.preventDefault();
            setOpen(false);
          }
        }}
        onFocus={(e: FocusEvent) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        onBlur={(e: FocusEvent) => {
          // Skip blur handling if we just opened the popover
          if (justOpenedRef.current) return;

          const { relatedTarget } = e;
          // Close if focus moves outside button/popover, or if relatedTarget is null (e.g., clicking into iframe)
          // Check gridContainerRef separately since Popover uses a portal
          if (
            !isInstance(relatedTarget, Node) ||
            (!buttonRef.current?.contains(relatedTarget) &&
              !popoverRef.current?.contains(relatedTarget) &&
              !gridContainerRef.current?.contains(relatedTarget) &&
              !menuRef.current?.contains(relatedTarget) &&
              !touchDialogRef.current?.contains(relatedTarget))
          ) {
            setOpen(false);
          }
        }}
        onClick={(e: MouseEvent) => {
          e.preventDefault();
          e.stopPropagation();
          setIsInTable(isInTableRef.current);
          if (!open) {
            justOpenedRef.current = true;
            setTimeout(() => {
              justOpenedRef.current = false;
            }, 100);
          }
          setOpen(!open);
        }}
        aria-expanded={open}
        aria-label={t('rte_table')}
        aria-haspopup
      >
        <Icon name='table' />
      </StyledTableButton>
      {buttonRef.current && (
        <Tooltip target={buttonRef.current} showDelay='none' hideDelay='none'>
          {t('rte_table')}
        </Tooltip>
      )}
      {isTouchDevice ? (
        <TouchDialog
          ref={touchDialogRef}
          open={open}
          heading={isInTable ? t('rte_table') : t('rte_insert_table')}
          onDismiss={() => {
            setFocusEditorAfterClose(false);
            setOpen(false);
          }}
          onAfterClose={() => {
            if (focusEditorAfterClose) {
              setFocusEditorAfterClose(false);
              editor.commands.focus();
            }
          }}
        >
          {tableContent}
        </TouchDialog>
      ) : (
        <Popover show={open} target={buttonRef.current} placement='bottom-start' ref={popoverRef}>
          {tableContent}
        </Popover>
      )}
    </>
  );
};

export default TableButton;
